import { Injectable } from "@angular/core";
import { PreviewCustomRule, createDefaultSTier, createDefaultATier, createDefaultBTier, createDefaultExcellentUniquesTier, createDefaultGoodUniquesTier, createDefaultPotentialUniquesTier, createDefaultOtherUniquesTier, createDefaultSearchingChanceBasesTier, createDefaultPotentialChanceBasesTier, createDefaultCTier, createDefaultDTier, createDefaultETier, createDefaultFTier, TierCosmetic, DisplayOptions, CosmeticPresets, Filter } from "../models/filter-models";
import { Color, DefaultLabelSize, LabelSize, Rarity } from "../models/enums";
import { ChanceBaseTierType, Tier, UniqueTierType } from "../models/item-data-models";
import { CurrencyTierService } from "./tiering/currency-tier.service";
import { UniquesTierService } from "./tiering/uniques-tier.service";
import { ChanceBasesTierService } from "./tiering/chance-bases-tier.service";

@Injectable({
  providedIn: 'root'
})
export class CosmeticPresetsService {
  static bgColor = '#ede9dee6';
  static textColor = '#1e1a11ff';
  static uniqueBgColor = '#af6025f0';
  static uniqueTextAndBorderColor = '#351e0eff';
  static divCardTextColor = '#013b53';
  static divCardBgColor = '#dbf5ffde';
  static divCardTextAndBorderColor = '#000000ff';

  static tieredTextColors: Record<Color, string> = {
    [Color.Purple]: '#4f007aff',
    [Color.Red]: '#990000ff',
    [Color.Orange]: '#7b430bff',
    [Color.Yellow]: '#534400ff',
    [Color.White]: '#000000ff',
    [Color.Green]: '#04480cff',
    [Color.Blue]: '#0c1c89ff',
    [Color.Pink]: '#8d2e7dff',
    [Color.Brown]: '#462813ff',
    [Color.Cyan]: '#0c5c53ff',
    [Color.Grey]: '#292929ff',
  };

  currencyRules: PreviewCustomRule[] = [];
  divCardRules: PreviewCustomRule[] = [];
  uniqueRules: PreviewCustomRule[] = [];
  currencyAndUniqueRules: PreviewCustomRule[] = [];
  allRules: PreviewCustomRule[] = [];

  constructor(private currencyTierService: CurrencyTierService,
    private uniquesTierService: UniquesTierService,
    private chanceBasesTierService: ChanceBasesTierService) {
  }

  generateUniqueCosmetics(cosmeticPresets: CosmeticPresets) {
    const excellentUniquesTier = createDefaultExcellentUniquesTier();
    excellentUniquesTier.displayOptions.cosmeticOptions!.textColor = cosmeticPresets.labelColor === 'default' ? undefined : CosmeticPresetsService.uniqueTextAndBorderColor;
    excellentUniquesTier.displayOptions.cosmeticOptions!.borderColor = cosmeticPresets.labelColor === 'default' ? undefined : CosmeticPresetsService.uniqueTextAndBorderColor;
    excellentUniquesTier.displayOptions.cosmeticOptions!.backgroundColor = cosmeticPresets.labelColor === 'default' ? undefined : CosmeticPresetsService.uniqueBgColor;
    excellentUniquesTier.displayOptions.cosmeticOptions!.fontSize = cosmeticPresets.labelSize === 'default' ? undefined : cosmeticPresets.labelSize === 'larger' ? LabelSize.Large : LabelSize.Largest;

    const goodUniquesTier = createDefaultGoodUniquesTier();
    if (cosmeticPresets.startingTier === 'aTier' || cosmeticPresets.startingTier === 'bTier' || cosmeticPresets.startingTier === 'cTier') {
      goodUniquesTier.displayOptions.cosmeticOptions!.textColor = cosmeticPresets.labelColor === 'default' ? undefined : CosmeticPresetsService.uniqueTextAndBorderColor;
      goodUniquesTier.displayOptions.cosmeticOptions!.borderColor = cosmeticPresets.labelColor === 'default' ? undefined : CosmeticPresetsService.uniqueTextAndBorderColor;
      goodUniquesTier.displayOptions.cosmeticOptions!.backgroundColor = cosmeticPresets.labelColor === 'default' ? undefined : CosmeticPresetsService.uniqueBgColor;
      goodUniquesTier.displayOptions.cosmeticOptions!.fontSize = cosmeticPresets.labelSize === 'default' ? undefined : cosmeticPresets.labelSize === 'larger' ? LabelSize.Large : LabelSize.Larger;
    }
    const searchingChanceBasesTier = createDefaultSearchingChanceBasesTier();
    searchingChanceBasesTier.displayOptions.cosmeticOptions!.fontSize = cosmeticPresets.labelSize === 'veryLarge' ? LabelSize.Large : undefined;

    return [excellentUniquesTier, goodUniquesTier, searchingChanceBasesTier];
  }

  generateUniqueRules(cosmeticPresets: CosmeticPresets, useExampleNames: boolean = false) {
    const [excellentUniquesTier, goodUniquesTier, searchingChanceBasesTier] = this.generateUniqueCosmetics(cosmeticPresets);

    const excellentUniquesRule = this.createPreviewRuleFromTierCosmetic(excellentUniquesTier, Rarity.Unique, false, true, false, useExampleNames ? undefined : excellentUniquesTier.name);
    const goodUniquesRule = this.createPreviewRuleFromTierCosmetic(goodUniquesTier, Rarity.Unique, false, true, false, useExampleNames ? undefined : goodUniquesTier.name);
    const potentialUniquesRule = this.createPreviewRuleFromTierCosmetic(createDefaultPotentialUniquesTier(), Rarity.Unique, false, true, false, useExampleNames ? undefined : createDefaultPotentialUniquesTier().name);
    const otherUniquesRule = this.createPreviewRuleFromTierCosmetic(createDefaultOtherUniquesTier(), Rarity.Unique, false, true, false, useExampleNames ? undefined : createDefaultOtherUniquesTier().name);
    const searchingChanceBasesRule = this.createPreviewRuleFromTierCosmetic(searchingChanceBasesTier, Rarity.Normal, false, false, true, useExampleNames ? undefined : searchingChanceBasesTier.name);
    const potentialChanceBasesRule = this.createPreviewRuleFromTierCosmetic(createDefaultPotentialChanceBasesTier(), Rarity.Normal, false, false, true, useExampleNames ? undefined : createDefaultPotentialChanceBasesTier().name);

    const rules = [excellentUniquesRule, goodUniquesRule, potentialUniquesRule, otherUniquesRule, searchingChanceBasesRule, potentialChanceBasesRule].map((rule, index) => ({
      ...rule,
      order: index,
    }));

    return rules;
  }

  generateCurrencyCosmetics(cosmeticPresets: CosmeticPresets) {
    return this.generateDefaultCosmetics(false, cosmeticPresets);
  }

  generateDivCardCosmetics(cosmeticPresets: CosmeticPresets) {
    return this.generateDefaultCosmetics(true, cosmeticPresets);
  }

  generateDefaultCosmetics(divCard: boolean, cosmeticPresets: CosmeticPresets) {
    const sTier = createDefaultSTier();
    sTier.displayOptions.cosmeticOptions!.textColor = cosmeticPresets.labelColor === 'default' ? undefined : this.getLabelColor(divCard, Color.Purple, cosmeticPresets);
    sTier.displayOptions.cosmeticOptions!.borderColor = cosmeticPresets.labelColor === 'default' ? undefined : this.getLabelColor(divCard, Color.Purple, cosmeticPresets);
    sTier.displayOptions.cosmeticOptions!.backgroundColor = cosmeticPresets.labelColor === 'default' ? undefined : (divCard ? CosmeticPresetsService.divCardBgColor : CosmeticPresetsService.bgColor);
    sTier.displayOptions.cosmeticOptions!.fontSize = cosmeticPresets.labelSize === 'default' ? undefined : cosmeticPresets.labelSize === 'larger' ? LabelSize.Large : LabelSize.Largest;

    const aTier = createDefaultATier();
    if (cosmeticPresets.startingTier === 'aTier' || cosmeticPresets.startingTier === 'bTier' || cosmeticPresets.startingTier === 'cTier') {
      aTier.displayOptions.cosmeticOptions!.textColor = cosmeticPresets.labelColor === 'default' ? undefined : this.getLabelColor(divCard, Color.Red, cosmeticPresets);
      aTier.displayOptions.cosmeticOptions!.borderColor = cosmeticPresets.labelColor === 'default' ? undefined : this.getLabelColor(divCard, Color.Red, cosmeticPresets);
      aTier.displayOptions.cosmeticOptions!.backgroundColor = cosmeticPresets.labelColor === 'default' ? undefined : (divCard ? CosmeticPresetsService.divCardBgColor : CosmeticPresetsService.bgColor);
      aTier.displayOptions.cosmeticOptions!.fontSize = cosmeticPresets.labelSize === 'default' ? undefined : cosmeticPresets.labelSize === 'larger' ? LabelSize.Large : LabelSize.Larger;
    }

    const bTier = createDefaultBTier();
    if (cosmeticPresets.startingTier === 'bTier' || cosmeticPresets.startingTier === 'cTier') {
      bTier.displayOptions.cosmeticOptions!.textColor = cosmeticPresets.labelColor === 'default' ? undefined : this.getLabelColor(divCard, Color.Orange, cosmeticPresets);
      bTier.displayOptions.cosmeticOptions!.borderColor = cosmeticPresets.labelColor === 'default' ? undefined : this.getLabelColor(divCard, Color.Orange, cosmeticPresets);
      bTier.displayOptions.cosmeticOptions!.backgroundColor = cosmeticPresets.labelColor === 'default' ? undefined : (divCard ? CosmeticPresetsService.divCardBgColor : CosmeticPresetsService.bgColor);
      bTier.displayOptions.cosmeticOptions!.fontSize = cosmeticPresets.labelSize === 'default' ? undefined : cosmeticPresets.labelSize === 'larger' ? LabelSize.Large : LabelSize.Large;
    }

    const cTier = createDefaultCTier();
    if (cosmeticPresets.startingTier === 'cTier') {
      cTier.displayOptions.cosmeticOptions!.textColor = cosmeticPresets.labelColor === 'default' ? undefined : this.getLabelColor(divCard, Color.Yellow, cosmeticPresets);
      cTier.displayOptions.cosmeticOptions!.borderColor = cosmeticPresets.labelColor === 'default' ? undefined : this.getLabelColor(divCard, Color.Yellow, cosmeticPresets);
      cTier.displayOptions.cosmeticOptions!.backgroundColor = cosmeticPresets.labelColor === 'default' ? undefined : (divCard ? CosmeticPresetsService.divCardBgColor : CosmeticPresetsService.bgColor);
    }

    return [sTier, aTier, bTier, cTier];
  }

  generateDivCardRules(cosmeticPresets: CosmeticPresets, useExampleNames: boolean = false) {
    return this.generateDefaultRules(true, cosmeticPresets, useExampleNames);
  }

  generateCurrencyRules(cosmeticPresets: CosmeticPresets, useExampleNames: boolean = false) {
    return this.generateDefaultRules(false, cosmeticPresets, useExampleNames);
  }

  generateDefaultRules(divCard: boolean, cosmeticPresets: CosmeticPresets, useExampleNames: boolean = false) {
    const [sTier, aTier, bTier, cTier] = this.generateDefaultCosmetics(divCard, cosmeticPresets);

    const sRule = this.createPreviewRuleFromTierCosmetic(sTier, Rarity.Normal, divCard, false, false, useExampleNames ? undefined : sTier.name);
    const aRule = this.createPreviewRuleFromTierCosmetic(aTier, Rarity.Normal, divCard, false, false, useExampleNames ? undefined : aTier.name);
    const bRule = this.createPreviewRuleFromTierCosmetic(bTier, Rarity.Normal, divCard, false, false, useExampleNames ? undefined : bTier.name);
    const cRule = this.createPreviewRuleFromTierCosmetic(cTier, Rarity.Normal, divCard, false, false, useExampleNames ? undefined : cTier.name);
    const dRule = this.createPreviewRuleFromTierCosmetic(createDefaultDTier(), Rarity.Normal, divCard, false, false, useExampleNames ? undefined : createDefaultDTier().name);
    const eRule = this.createPreviewRuleFromTierCosmetic(createDefaultETier(), Rarity.Normal, divCard, false, false, useExampleNames ? undefined : createDefaultETier().name);
    const fRule = this.createPreviewRuleFromTierCosmetic(createDefaultFTier(), Rarity.Normal, divCard, false, false, useExampleNames ? undefined : createDefaultFTier().name);

    const rules = [sRule, aRule, bRule, cRule, dRule, eRule, fRule].map((rule, index) => ({
      ...rule,
      order: index,
    }));

    return rules;
  }

  getLabelColor(divCard: boolean, color: Color, cosmeticPresets: CosmeticPresets) {
    return cosmeticPresets.labelColor === 'default'
      ? undefined
      : cosmeticPresets.labelColor === 'accentNeutral'
        ? (divCard ? CosmeticPresetsService.divCardTextColor : CosmeticPresetsService.textColor)
        : CosmeticPresetsService.tieredTextColors[color];
  }

  createPreviewRuleFromTierCosmetic(tierCosmetic: TierCosmetic, rarity: Rarity, divCard: boolean, unique: boolean, chanceBase: boolean, exampleName?: string) {
    const rule = new PreviewCustomRule({
      exampleName: exampleName || (unique
        ? this.getFirstUniqueForDropTier(tierCosmetic.uniqueTier!)
        : chanceBase
          ? this.getFirstChanceBaseForDropTier(tierCosmetic.chanceBaseTier!)
          : this.currencyTierService.getFirstCurrencyForDropTier(tierCosmetic.tier!, divCard)
            ?? tierCosmetic.tier! + '-Tier'),
      displayOptions: tierCosmetic.displayOptions,
      rarity: rarity,
      itemClass: rarity ? 'All' : 'Stackable Currency',
      isCurrency: !divCard && !unique && !chanceBase,
      isDivinationCard: divCard,
    });

    return rule;
  }

  getFirstUniqueForDropTier(tier: UniqueTierType): string | undefined {
    const tierData = this.uniquesTierService.getTieredUniques()[tier];
    return tierData[0]?.baseType ?? "Unique";
  }

  getFirstChanceBaseForDropTier(tier: ChanceBaseTierType): string | undefined {
    const tierData = this.chanceBasesTierService.getTieredChanceBases()[tier];
    return tierData[0]?.baseType ?? "Chance Base";
  }

  regenerateRules(filter: Filter, useExampleNames: boolean = false) {
    const currencyRules = this.generateCurrencyRules(filter.cosmeticPresets, useExampleNames);
    const divCardRules = this.generateDivCardRules(filter.cosmeticPresets, useExampleNames);
    const uniqueRules = this.generateUniqueRules(filter.cosmeticPresets, useExampleNames);

    const allRulesWithUpdatedOrder = [...currencyRules, ...divCardRules, ...uniqueRules].map((rule, index) => ({
      ...rule,
      order: index,
    }));

    const currencyAndUniqueRulesWithUpdatedOrder = [...currencyRules, ...uniqueRules].map((rule, index) => ({
      ...rule,
      order: index,
    }));

    this.currencyRules.length = 0;
    this.divCardRules.length = 0;
    this.uniqueRules.length = 0;
    this.allRules.length = 0;
    this.currencyAndUniqueRules.length = 0;
    this.currencyRules.push(...currencyRules);
    this.divCardRules.push(...divCardRules);
    this.uniqueRules.push(...uniqueRules);
    this.allRules.push(...allRulesWithUpdatedOrder);
    this.currencyAndUniqueRules.push(...currencyAndUniqueRulesWithUpdatedOrder);
  }

  setLabelSize(size: 'default' | 'larger' | 'veryLarge', filter: Filter) {
    filter.cosmeticPresets.labelSize = size;
    this.regenerateRules(filter);
  }

  setLabelColor(color: 'default' | 'accentNeutral' | 'accentTiered', filter: Filter) {
    filter.cosmeticPresets.labelColor = color;
    this.regenerateRules(filter);
  }

  setStartingTier(tier: 'sTier' | 'aTier' | 'bTier' | 'cTier', filter: Filter) {
    filter.cosmeticPresets.startingTier = tier;
    this.regenerateRules(filter);
  }

  setDefaultLabelSize(size: DefaultLabelSize, filter: Filter) {
    filter.cosmeticPresets.defaultLabelSize = size;
    this.regenerateRules(filter);
  }

  applyPreset(filter: Filter) {
    const [sTier, aTier, bTier, cTier] = this.generateDefaultCosmetics(false, filter.cosmeticPresets);
    const [sDivCardTier, aDivCardTier, bDivCardTier, cDivCardTier] = this.generateDefaultCosmetics(true, filter.cosmeticPresets);
    const [excellentUniquesTier, goodUniquesTier, searchingChanceBasesTier] = this.generateUniqueCosmetics(filter.cosmeticPresets);

    const sTierCosmetic = filter.tierCosmetics?.find(tc => tc.tier === Tier.SPurple);
    if (sTierCosmetic) sTierCosmetic.displayOptions = new DisplayOptions({ ...sTierCosmetic.displayOptions, cosmeticOptions: sTier.displayOptions.cosmeticOptions });

    const aTierCosmetic = filter.tierCosmetics?.find(tc => tc.tier === Tier.ARed);
    if (aTierCosmetic) aTierCosmetic.displayOptions = new DisplayOptions({ ...aTierCosmetic.displayOptions, cosmeticOptions: aTier.displayOptions.cosmeticOptions });

    const bTierCosmetic = filter.tierCosmetics?.find(tc => tc.tier === Tier.BOrange);
    if (bTierCosmetic) bTierCosmetic.displayOptions = new DisplayOptions({ ...bTierCosmetic.displayOptions, cosmeticOptions: bTier.displayOptions.cosmeticOptions });

    const cTierCosmetic = filter.tierCosmetics?.find(tc => tc.tier === Tier.CYellow);
    if (cTierCosmetic) cTierCosmetic.displayOptions = new DisplayOptions({ ...cTierCosmetic.displayOptions, cosmeticOptions: cTier.displayOptions.cosmeticOptions });

    const sDivCardTierCosmetic = filter.divCardTierCosmetics?.find(tc => tc.tier === Tier.SPurple);
    if (sDivCardTierCosmetic) sDivCardTierCosmetic.displayOptions = new DisplayOptions({ ...sDivCardTierCosmetic.displayOptions, cosmeticOptions: sDivCardTier.displayOptions.cosmeticOptions });

    const aDivCardTierCosmetic = filter.divCardTierCosmetics?.find(tc => tc.tier === Tier.ARed);
    if (aDivCardTierCosmetic) aDivCardTierCosmetic.displayOptions = new DisplayOptions({ ...aDivCardTierCosmetic.displayOptions, cosmeticOptions: aDivCardTier.displayOptions.cosmeticOptions });

    const bDivCardTierCosmetic = filter.divCardTierCosmetics?.find(tc => tc.tier === Tier.BOrange);
    if (bDivCardTierCosmetic) bDivCardTierCosmetic.displayOptions = new DisplayOptions({ ...bDivCardTierCosmetic.displayOptions, cosmeticOptions: bDivCardTier.displayOptions.cosmeticOptions });

    const cDivCardTierCosmetic = filter.divCardTierCosmetics?.find(tc => tc.tier === Tier.CYellow);
    if (cDivCardTierCosmetic) cDivCardTierCosmetic.displayOptions = new DisplayOptions({ ...cDivCardTierCosmetic.displayOptions, cosmeticOptions: cDivCardTier.displayOptions.cosmeticOptions });

    const excellentUniquesTierCosmetic = filter.uniqueTierCosmetics?.find(tc => tc.uniqueTier === 'Excellent');
    if (excellentUniquesTierCosmetic) excellentUniquesTierCosmetic.displayOptions = new DisplayOptions({ ...excellentUniquesTierCosmetic.displayOptions, cosmeticOptions: excellentUniquesTier.displayOptions.cosmeticOptions });

    const goodUniquesTierCosmetic = filter.uniqueTierCosmetics?.find(tc => tc.uniqueTier === 'Good');
    if (goodUniquesTierCosmetic) goodUniquesTierCosmetic.displayOptions = new DisplayOptions({ ...goodUniquesTierCosmetic.displayOptions, cosmeticOptions: goodUniquesTier.displayOptions.cosmeticOptions });

    const searchingChanceBasesTierCosmetic = filter.chanceBaseTierCosmetics?.find(tc => tc.chanceBaseTier === 'Searching');
    if (searchingChanceBasesTierCosmetic) searchingChanceBasesTierCosmetic.displayOptions = new DisplayOptions({ ...searchingChanceBasesTierCosmetic.displayOptions, cosmeticOptions: searchingChanceBasesTier.displayOptions.cosmeticOptions });
  }
}
